import { Conversation } from '@src/services/walle';
import {
  SchedulerFormSubmitPayload,
  SchedulerIntentClarification,
  SchedulerRunNotificationPayload,
  SchedulerTaskCardPayload,
} from './scheduler-message';

const stopRawValue = 'passive_termination';
const manualStopRawValue = 'termination';

export enum GlobalEventType {
  USER_REJECT = 'global_event_userReject',
  SCHEDULER_FORM_SUBMIT = 'global_event_schedulerFormSubmit',
  SCHEDULER_FORM_SKIP = 'global_event_schedulerFormSkip',
  SCHEDULER_EDIT_TASK = 'global_event_schedulerEditTask',
  SCHEDULER_VIEW_ALL_TASK = 'global_event_schedulerViewAllTask',
  CONVERSATION_EVENTS_REFRESH = 'global_event_conversationEventsRefresh',
  DEEP_RESEARCH_CLAR_SUBMIT = 'global_event_deepResearchClarSubmit',
  SET_TEMPLATE = 'global_event_setTemplate',
}

export interface ConversationEventsRefreshPayload {
  conversationId: string;
  reason?: string;
  source?: string;
}

export interface IConversationData {
  conversationName: string;
  conversationStatus: ConversationStatus;
  events: MessageEventDTO[];
}

export interface IChatFile {
  fileExtension: string;
  fileName: string;
  fileSize: number;
  fileId: string;
  pdfKey?: string;
  fileUrl?: string;
}

export interface IFileGenerate {
  file_name: string;
  file_id: string;
  file_size: number;
  file_extension: string;
  pdf_key?: string;
}

export interface IDeepResearchClarFormItem {
  id: string;
  type: 'text';
  label: string;
  value: string;
}

export interface IDeepResearchClarFormAction {
  text: string;
  type: 'button';
  variant: 'primary';
  disabled: boolean;
}

export interface IDeepResearchClarification {
  type: 'deep_research_preferences',
  title: string;
  formItems: IDeepResearchClarFormItem[];
  actions: IDeepResearchClarFormAction[];
}

export interface RefDetail {
  image_url?: string;
  json_output?: string;
  markdown_content?: string;
  coder?: {
    content?: string;
    language?: string;
  };
  search_web_list?: SearchWebItem[];
  steps?: IStep[];
  file?: IFileGenerate;
  intent_clarification?: IDeepResearchClarification | SchedulerIntentClarification;
  scheduler_task_card?: SchedulerTaskCardPayload;
  scheduler_run_notification?: SchedulerRunNotificationPayload;
}

export interface IStep {
  content: string;
  status: StepProcessStatus;
}

export interface SearchWebItem {
  url?: string;
  icon?: string;
  title?: string;
  content?: string;
}

export interface MessageEventDTO {
  attachments?: Array<IChatFile>;
  content: string;
  id: string;
  replace_id?: string;
  sender: MessageRole;
  timestamp: number;
  type: MessageEventType;
  status?: MessageStatus;
  title?: string;
  icon?: string;
  tmpContents?: MessageEventDTO[];
  tool?: EventToolType;
  ref_detail?: RefDetail;
  need_help_type?: NeedUserHelpType;
  user_help_type?: NeedUserHelpType;
  help_id?: string;
  operate_type?: UserOperatedType;
  runId: string;
  plan_step_id?: string;
}

export enum EventToolType {
  BROWSER = 'browser',
  MCP = 'mcp',
  TODO_LIST = 'todolist',
  CODE = 'coder',
  SEARCH_WEB_LIST = 'search_web_list',
  MARKDOWN = 'markdown',
  DEEP_SEARCH = 'deepSearch',
  FILE_ANALYSIS = 'fileAnalyse',
  IMAGE = 'image',
  VNC = 'vnc',
  FILE_GENERATE = 'file',
  INTENT_CLARIFICATION = 'intent_clarification',
  SCHEDULER_TASK_CARD = 'scheduler_task_card',
  SCHEDULER_RUN_NOTIFICATION = 'scheduler_run_notification',
}

export enum MessageEventType {
  CHAT = 'chat',
  STEP_FOLD = 'stepFold',
  NEW_STEP = 'newPlanStep',
  STEP_FINISH = 'stepFinish',
  STEP_TEXT = 'stepText',
  TOOL_USED = 'toolUsed',
  NEED_USER_HELP = 'needUserHelp',
  USER_HELP = 'user_help',
  USER_OPERATED = 'userOperated',
  LIVE_STATUS = 'status',
  STEP_LIST_UPDATE = 'planStepList',
}

export enum UserOperatedType {
  SKIP = 'skip',
  DONE = 'done',
}

export enum NeedUserHelpType {
  LOGIN = 'login',
}

export type SchedulerFormSubmitEventPayload = SchedulerFormSubmitPayload;

export enum MessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
}

export enum ConversationStatus {
  COMPLETED = 'completed',
  RUNNING = 'running',
  STOP = stopRawValue,
  MANUAL_STOP = manualStopRawValue,
}

export enum DrawerMode {
  HOVER = 'hover',
  ALWAYS = 'always',
  HIDE = 'hide',
}

export enum StepProcessStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  DONE = 'completed',
  STOP = stopRawValue,
  MANUAL_STOP = manualStopRawValue,
}

export enum MessageType {
  ANSWER = 'ANSWER',
  QUESTION = 'QUESTION',
}

export enum MessageStatus {
  DONE = 'completed',
  ERROR = 'error',
  PENDING = 'pending',
  STREAMING = 'streaming',
  PAUSED = 'paused',
}

export enum TaskTriggerType {
  ONCE = 'once',
  EVERY_DAY = 'every_day',
  EVERY_WEEK = 'every_week',
  EVERY_MONTH = 'every_month',
}

export interface ComputerDataItem {
  id: string;
  type: EventToolType;
  icon?: string;
  title?: string;
  data?: RefDetail;
  toolName?: string;
}

export enum FileType {
  /** 在线文档 */
  ONLINE_DOC = 'online_doc',
  ONLINE_EXCEL = 'online_excel',
  /** 文件 */
  PDF = 'pdf',
  WORD = 'word',
  EXCEL = 'excel',
  CSV = 'csv',
  /** 代码 */
  CODE = 'code',
  /** 图片 */
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  TEXT = 'txt',
  PPT = 'ppt',
  MARKDOWN = 'markdown',
  PSD = 'psd',
  KEY = 'key',
}

export type TSessionItem = Conversation;

export enum ListTypeEnum {
  LIST = 'list',
  TITLE = 'title',
}

export type TSessionListGroupTitle = {
  type: ListTypeEnum.TITLE;
  title: string;
};

export type TSessionListItem = {
  type: ListTypeEnum.LIST;
} & TSessionItem;

export type TSessionList = Array<TSessionListItem | TSessionListGroupTitle>;

// 首页中的 创作、代码、分析等块
export type CaseItem = {
  title: string;
  icon: string;
  id: string;
};

export interface IFeedbackData {
  conversation_id: string;
  create_time: string;
  feedback_id: string;
  feedback_tags: string[];
  feedback_text: string;
  feedback_type: string;
  run_id: string;
  user_id: number;
}

export type ConversationTagType = 'internal_audit' | 'risk_detection' | '';

export type PromptType = 'deep_research' | 'scheduler' | 'website';

export interface TemplateItem {
  template_id: string;
  name: string;
  category: PromptType;
  cover_url: string;
  usage_count: number;
  view_count: number;
  example: string;
  prompt: string;
}
